 <template>
    <div class="container" id="adset-editor" dusk="adset-edit">
        <div class="col-md-12 text-center" v-if="loading">
            <i class="fa fa-spinner fa-spin inner-loading"></i>
        </div>

        <div class="row" dusk="ready" v-else>
            <div class="col-sm-10 col-sm-offset-1">
                <div class="row page-title">
                    <div class="col-md-12">
                        <h1>Edit {{ campaign.ad_product.type }} Ad Set</h1>
                    </div>
                </div>

                <form id="adSetForm">
                    <campaign-adset-editor
                        v-model="adSet"
                        :campaign="campaign"
                        :dimensions="dimensions"
                        :loan-sizes="loanSizes"
                        :base-url="baseUrl"
                        :validation="validation"
                        :features="features"
                        v-on:validate="setValidation"
                        :enable-loan-sizes="enableLoanSizes"
                        :show-errors="showErrors"
                        :phone-numbers="phoneNumbers"
                        :user-roles="userRoles"
                        @scheduleHasOverlaps="setScheduleHasOverlaps"
                        ref="adSetEditor">
                    </campaign-adset-editor>

                    <div class="row buttons" v-show="adSet.id != null" v-cloak>
                        <div class="col-md-5">
                            <button id="save_ad_set" @click.prevent="saveAndReturn()" type="button" class="btn btn-block --gray --gray-border">Save Ad Set
                            </button>
                            <p>Save progress and go back to the summary page.</p>
                        </div>

                        <div class="col-md-5 col-md-offset-2">
                          <button id="go_back_campaign_manager" v-on:click="goBackLink()" type="button" class="btn btn-block --gray --gray-border">Go Back</button>
                          <p>Go back to the campaign manager.</p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import campaignCommon from 'js/campaigns/common'
import adSetEditor from './editor'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'

export default {
    mixins: [campaignCommon, adSetEditor],

    props: ['campaign', 'adSetData', 'dimensions', 'enable-loan-sizes', 'phoneNumbers', 'features', 'userRoles'],

    data() {
        return {
            loading: true,
            adSet: null,
            currentAdSetNames: [],
            showErrors: false,
            baseUrl: Laravel.baseUrl,
            loanSizes: [],
            validationFields: {
                locations: false,
                attributes: true
            },
            scheduleHasOverlaps: false
        }
    },

    mounted() {
        this.adSetData.start_date = this.adSetData.start_date ? moment(this.adSetData.start_date).format('MM/DD/YYYY') : '';
        this.adSetData.end_date = this.adSetData.end_date ? moment(this.adSetData.end_date).format('MM/DD/YYYY') : '';

        if (has(this.adSetData, 'phone.id')) {
            const phone = this.adSetData.phone
            this.adSetData.phone = {
                id: phone.id,
                label: `${phone.label} | ${phone.leasedPhoneNumberFormatted} | ${phone.advertiserPhoneNumberFormatted}`
            }
        } else {
            this.adSetData.phone = null
        }

        this.adSet = this.adSetData;

        Promise.all([
            this.getCurrentNames(),
            this.getLoanSizes()
        ]).then(() => {
            var position = _.indexOf(this.currentAdSetNames, this.adSet.name.trim().toLowerCase());
            if (position != -1) {
                this.currentAdSetNames.splice(position, 1);
            }
            this.loading = false;
        });
    },

    methods: {
        save: function (shouldRedirectToPreview) {
            if (!this.isValid) {
                this.showErrors = true;
                return false;
            }

            $.loading.show();

            //multiselect returns mixed type; wrap in array in case a single object has been returned.
            if (!Array.isArray(this.adSet.loan_purposes)) {
                this.adSet.loan_purposes = [this.adSet.loan_purposes];
            }

            const fields = {
                ...this.adSet,
                product_category_id: this.campaign.product_category.id,
                vertical: this.campaign.vertical
            };

            if (this.adSet.expanded_attributes) {
                const expandedAttributes = Object.entries(this.adSet.expanded_attributes).reduce((acc, [key, value]) => {
                    if (!isEmpty(value)) {
                        acc.push({
                            name: key,
                            value
                        });
                    }
                    return acc;
                }, []);

                fields.attributes = fields.attributes.concat(expandedAttributes);
            }

            this.$http.put('/campaigns/' + this.campaign.id + '/adset/' + this.adSet.id, fields).then(function (response) {
                    $.loading.hide();
                    if (response.body.success) {
                        const goToUrl = shouldRedirectToPreview ? route('campaign.adset.preview', [this.campaign.id, response.body.adSet.id]) : route('campaigns.show', this.campaign.id);

                        let message = 'Ad set saved successfully!';
                        if (response.body.pendingBudget) {
                            message += "<br><span class='text-danger'>Pending budget changes will take effect at 8 AM.</span>"
                        }

                        this.showSuccessMessage(message, this.redirect.bind(null, goToUrl));
                    } else {
                        this.notifyRollbar('Edit AdSet Failure: components/adsets/edit.vue@save: line 101', response);
                        this.showErrorMsg('Error encountered while saving the ad set.');
                    }
                },
                function (errorResponse) {
                    $.loading.hide();
                    this.notifyRollbar('Edit AdSet Failure: components/adsets/edit.vue@save: line 101', errorResponse);
                    this.manageErrors(errorResponse);
                });
        },

        saveAndReturn() {
            if (!this.isValid) {
                this.showErrors = true;
            }

            this.$refs.adSetEditor.$validator.validateAll().then((result) => {
                if (result && this.isValid && this.hasValidPhone && this.hasValidSchedule) {
                    this.save(false);
                }
            })
        },

        goBackLink: function () {
          window.location = route('campaigns.show', this.campaign.id);
        },

        setScheduleHasOverlaps: function (status) {
            this.scheduleHasOverlaps = status;
        }
    },

    computed: {
        isHomeEquity() {
            return this.campaign.product_category.id === 20;
        },

        hasValidPhone() {
            const checkPhoneNumber = this.features.ad_set.phone && Number(_.get(this.adSet, 'type.id')) === 2;
            if (!checkPhoneNumber) {
                return true;
            }

            return _.has(this.adSet, 'phone.id');
        },

        hasValidSchedule() {
            const haveToCheck = this.features.ad_set.phone && Number(_.get(this.adSet, 'type.id')) === 2;
            if (!haveToCheck) {
                return true;
            }

            return !this.scheduleHasOverlaps;
        }
    }
}
</script>
